import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, Users, BarChart3, Trash2,
  CheckCircle, Mail, Phone, Globe, MapPin,
  TrendingUp, Eye, AlertCircle, Edit, Plus, ChevronRight,
  Calendar, Target, Trophy, RefreshCw, Search, Shield, BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const OrgDetailPage = ({ user }) => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [allUsers, setAllUsers] = useState([]);
  const [showWinsWall, setShowWinsWall] = useState(false);
  const [winsData, setWinsData] = useState([]);
  const [showGoalsFeed, setShowGoalsFeed] = useState(false);
  const [goalsData, setGoalsData] = useState([]);
  const [showQAFeed, setShowQAFeed] = useState(false);
  const [qaData, setQaData] = useState([]);

  useEffect(() => {
    fetchOrgDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const fetchOrgDetail = async () => {
    try {
      const res = await fetch(`${API}/api/platform/organizations/${orgId}/detail`, { headers: hdr() });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setEditForm({
          name: d.organization.name || '',
          phone: d.organization.phone || '',
          website: d.organization.website || '',
          address: typeof d.organization.address === 'string' ? d.organization.address : '',
          primary_contact_name: d.organization.primary_contact_name || '',
          primary_contact_email: d.organization.primary_contact_email || '',
          primary_contact_title: d.organization.primary_contact_title || '',
          payment_status: d.subscription?.payment_status || d.subscription?.status || ''
        });
        // Fetch all users for this org
        const programs = d.programs.map(p => p.id);
        if (programs.length > 0) {
          const uRes = await fetch(`${API}/api/platform/programs/${programs[0]}/users`, { headers: hdr() });
          if (uRes.ok) { const ud = await uRes.json(); setAllUsers(ud.users || []); }
        }
      }
    } catch (e) { toast.error('Connection error'); }
    setLoading(false);
  };

  const saveOrgEdit = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/platform/organizations/${orgId}`, { method: 'PUT', headers: hdr(), body: JSON.stringify(editForm) });
      toast.success('Organization updated!');
      setShowEdit(false);
      fetchOrgDetail();
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  };

  const deleteOrg = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/platform/organizations/${orgId}`, { method: 'DELETE', headers: hdr() });
      if (res.ok) { toast.success('Organization archived'); navigate('/platform-admin'); }
    } catch (e) { toast.error('Failed'); }
    setDeleting(false);
  };

  const loadWinsWall = async () => {
    try {
      const res = await fetch(`${API}/api/admin/wins-wall-view`, { headers: hdr() });
      if (res.ok) { const d = await res.json(); setWinsData(d.posts || []); setShowWinsWall(true); }
    } catch (e) { toast.error('Failed to load wins'); }
  };

  const loadGoals = async () => {
    try {
      const res = await fetch(`${API}/api/reports/goals`, { headers: hdr() });
      if (res.ok) { const blob = await res.blob(); /* Goals come as CSV, use admin endpoint instead */ }
    } catch (e) {}
    // Use the completion tracker data instead
    setShowGoalsFeed(true);
  };

  const loadQA = async () => {
    try {
      const res = await fetch(`${API}/api/admin/qa-conversations`, { headers: hdr() });
      if (res.ok) { const d = await res.json(); setQaData(d.questions || []); setShowQAFeed(true); }
    } catch (e) { toast.error('Failed to load Q&A'); }
  };

  const viewAsOrgAdmin = async () => {
    // Find an admin for this org
    const admin = allUsers.find(u => u.role === 'program_admin' || u.role === 'admin');
    if (!admin) { toast.error('No admin account found for this org'); return; }
    try {
      const res = await fetch(`${API}/api/platform/view-as/${admin.id}`, { method: 'POST', headers: hdr() });
      if (res.ok) {
        const d = await res.json();
        localStorage.setItem('original_admin_token', localStorage.getItem('token'));
        localStorage.setItem('impersonating', 'true');
        localStorage.setItem('token', d.token);
        toast.success(`Viewing as ${d.user.name}`);
        window.location.href = '/admin';
      }
    } catch (e) { toast.error('Failed to impersonate'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}><RefreshCw className="w-8 h-8 animate-spin text-gray-300" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}><p className="text-gray-400">Organization not found</p></div>;

  const org = data.organization;
  const users = data.users;
  const engagement = data.engagement;

  // Filter users
  let filteredUsers = allUsers;
  if (userSearch) filteredUsers = filteredUsers.filter(u => (u.name || '').toLowerCase().includes(userSearch.toLowerCase()) || (u.email || '').toLowerCase().includes(userSearch.toLowerCase()));
  if (userRoleFilter !== 'all') filteredUsers = filteredUsers.filter(u => u.role === userRoleFilter);

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1B2D4F 0%, #1E6B7B 100%)' }} className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/platform-admin')} className="p-2 rounded-full bg-white/20 hover:bg-white/30"><ArrowLeft className="w-5 h-5 text-white" /></button>
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center text-2xl">
                {org.logo_url ? <img src={org.logo_url} alt="" className="w-10 h-10 rounded-xl object-contain" /> : <span className="text-white font-bold text-xl">{(org.name || '?').charAt(0)}</span>}
              </div>
              <div>
                <h1 className="text-2xl font-bold" style={{ color: 'white' }}>{org.name || org.slug}</h1>
                <p style={{ color: 'rgba(255,255,255,0.7)' }} className="text-sm">{org.city || ''}{org.state ? `, ${org.state}` : ''}</p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={viewAsOrgAdmin} size="sm" className="bg-orange-500 hover:bg-orange-600 rounded-lg text-xs gap-1"><Eye className="w-3.5 h-3.5" /> View As Client</Button>
              <Button onClick={() => setShowEdit(true)} size="sm" variant="outline" className="text-white border-white/30 hover:bg-white/10 rounded-lg text-xs gap-1"><Edit className="w-3.5 h-3.5" /> Edit Org</Button>
              <Badge className={org.status === 'active' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}>{org.status}</Badge>
              <Button onClick={() => setShowDelete(true)} variant="ghost" size="sm" className="text-red-300 hover:bg-red-500/20"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        <Tabs defaultValue="overview">
          <TabsList className="bg-white rounded-xl p-1 shadow-sm mb-6">
            <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-[#1B2D4F] data-[state=active]:text-white">Overview</TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-[#1B2D4F] data-[state=active]:text-white">Users ({allUsers.length})</TabsTrigger>
            <TabsTrigger value="programs" className="rounded-lg data-[state=active]:bg-[#1B2D4F] data-[state=active]:text-white">Programs ({data.programs.length})</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview">
            <div className="space-y-5">
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { l: 'Total Users', v: users.total, c: '#3B82F6', icon: Users, onClick: () => { document.querySelector('[value="users"]')?.click(); } },
                  { l: 'Youth', v: users.by_role?.youth || 0, c: '#10B981', icon: Users, onClick: () => { setUserRoleFilter('youth'); document.querySelector('[value="users"]')?.click(); } },
                  { l: 'Staff', v: (users.by_role?.lead_monitor || 0) + (users.by_role?.coach || 0), c: '#F59E0B', icon: Shield, onClick: () => { setUserRoleFilter('lead_monitor'); document.querySelector('[value="users"]')?.click(); } },
                  { l: 'Never Logged In', v: users.never_logged_in, c: '#EF4444', icon: AlertCircle, onClick: () => { setUserRoleFilter('all'); document.querySelector('[value="users"]')?.click(); } },
                  { l: 'Goals', v: engagement.total_goals, c: '#8B5CF6', icon: Target, onClick: () => navigate('/admin/completion-tracker') },
                  { l: 'Wins', v: engagement.total_wins, c: '#E86C3D', icon: Trophy, onClick: loadWinsWall },
                ].map(k => (
                  <Card key={k.l} className="p-4 rounded-2xl bg-white text-center cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" onClick={k.onClick} data-testid={`kpi-${k.l.toLowerCase().replace(/ /g,'-')}`}>
                    <k.icon className="w-5 h-5 mx-auto mb-1" style={{ color: k.c }} />
                    <p className="text-2xl font-black" style={{ color: k.c }}>{k.v}</p>
                    <p className="text-[10px] text-gray-500">{k.l}</p>
                  </Card>
                ))}
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                {/* Contact Info */}
                <Card className="p-5 rounded-2xl bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2"><Users className="w-4 h-4" /> Contact Information</h3>
                    <Button onClick={() => setShowEdit(true)} size="sm" variant="ghost" className="text-xs text-gray-400"><Edit className="w-3 h-3 mr-1" /> Edit</Button>
                  </div>
                  <div className="space-y-2 text-sm">
                    {org.primary_contact_name ? <p className="flex items-center gap-2"><Users className="w-3.5 h-3.5 text-gray-400" /> <strong>{org.primary_contact_name}</strong> {org.primary_contact_title && `— ${org.primary_contact_title}`}</p> : null}
                    {org.primary_contact_email ? <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-gray-400" /> {org.primary_contact_email}</p> : null}
                    {org.phone ? <p className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-gray-400" /> {org.phone}</p> : null}
                    {org.website ? <p className="flex items-center gap-2"><Globe className="w-3.5 h-3.5 text-gray-400" /> <a href={org.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{org.website}</a></p> : null}
                    {org.address && typeof org.address === 'string' ? <p className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-gray-400" /> {org.address}</p> : null}
                    {!org.primary_contact_name && !org.phone && !org.website && <p className="text-gray-400 italic">No contact info — <button onClick={() => setShowEdit(true)} className="text-orange-500 underline">add now</button></p>}
                  </div>
                </Card>

                {/* Billing */}
                <Card className="p-5 rounded-2xl bg-white">
                  <h3 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Billing</h3>
                  {data.subscription ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-gray-500">Plan:</span><span className="font-bold">{data.subscription.plan?.toUpperCase()}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Status:</span><Badge className={data.subscription.payment_status === 'free_pilot' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>{data.subscription.payment_status || data.subscription.status}</Badge></div>
                      <div className="flex justify-between"><span className="text-gray-500">Seats:</span><span>{data.subscription.seats}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Price:</span><span className="font-bold">${data.subscription.price?.toLocaleString()}/{data.subscription.billing_cycle === 'annual' ? 'yr' : 'mo'}</span></div>
                      {data.subscription.notes && <p className="text-gray-400 italic text-xs mt-2">{data.subscription.notes}</p>}
                    </div>
                  ) : (
                    <p className="text-gray-400">No subscription — <button onClick={() => navigate('/platform-admin')} className="text-orange-500 underline">add in Billing tab</button></p>
                  )}
                </Card>
              </div>

              {/* Recent Activity */}
              <Card className="p-5 rounded-2xl bg-white">
                <h3 className="font-bold text-sm text-gray-900 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Recent Logins</h3>
                {users.recent_logins?.length > 0 ? (
                  <div className="space-y-1.5">
                    {users.recent_logins.map((u, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-xs">{(u.name || '?').charAt(0)}</div>
                          <div>
                            <span className="font-semibold">{u.name}</span>
                            <Badge className="ml-2 text-[9px]" variant="outline">{u.role}</Badge>
                          </div>
                        </div>
                        <span className="text-xs text-gray-500">{u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-center py-4">No logins recorded yet</p>
                )}
                <p className="text-xs text-red-400 mt-3 font-medium">{users.never_logged_in} users have never logged in</p>
              </Card>
            </div>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users">
            <div className="space-y-4">
              {/* Search and Filters */}
              <div className="flex gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search users..." className="pl-9 rounded-xl bg-white" />
                </div>
                {['all', 'youth', 'lead_monitor', 'program_admin', 'parent'].map(r => (
                  <Button key={r} size="sm" onClick={() => setUserRoleFilter(r)} variant={userRoleFilter === r ? 'default' : 'outline'}
                    className={`rounded-xl text-xs ${userRoleFilter === r ? 'bg-[#1B2D4F] text-white' : ''}`}>
                    {r === 'all' ? 'All' : r === 'lead_monitor' ? 'Monitors' : r === 'program_admin' ? 'Admins' : r.charAt(0).toUpperCase() + r.slice(1)} ({r === 'all' ? allUsers.length : allUsers.filter(u => u.role === r).length})
                  </Button>
                ))}
              </div>

              {/* User Table */}
              <Card className="rounded-2xl bg-white overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-gray-50 text-xs text-gray-600">
                      <th className="p-3 text-left">Name</th>
                      <th className="p-3 text-left">Email</th>
                      <th className="p-3 text-center">Role</th>
                      <th className="p-3 text-center">Points</th>
                      <th className="p-3 text-center">Last Login</th>
                      <th className="p-3 text-center">Status</th>
                    </tr></thead>
                    <tbody>
                      {filteredUsers.slice(0, 100).map(u => (
                        <tr key={u.id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => u.role === 'youth' ? navigate(`/admin/youth/${u.id}`) : null}>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-xs">{(u.name || '?').charAt(0)}</div>
                              <span className="font-semibold">{u.name}</span>
                            </div>
                          </td>
                          <td className="p-3 text-gray-500 text-xs">{u.email}</td>
                          <td className="p-3 text-center"><Badge variant="outline" className="text-[10px]">{u.role}</Badge></td>
                          <td className="p-3 text-center font-bold text-orange-600">{u.total_points || 0}</td>
                          <td className="p-3 text-center text-xs text-gray-400">{u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}</td>
                          <td className="p-3 text-center">
                            <div className={`w-2 h-2 rounded-full mx-auto ${u.last_login ? 'bg-green-500' : 'bg-gray-300'}`} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredUsers.length === 0 && <p className="text-center py-6 text-gray-400">No users match your search</p>}
                {filteredUsers.length > 100 && <p className="text-center py-2 text-xs text-gray-400">Showing first 100 of {filteredUsers.length}</p>}
              </Card>
            </div>
          </TabsContent>

          {/* PROGRAMS TAB */}
          <TabsContent value="programs">
            <div className="space-y-4">
              {data.programs.map(p => (
                <Card key={p.id} className="p-5 rounded-2xl bg-white hover:shadow-md transition-all cursor-pointer" onClick={() => navigate(`/admin/program/${p.id}`)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">{p.display_name || p.branding?.display_name || p.name}</h4>
                        <p className="text-xs text-gray-500">{p.program_type?.toUpperCase()} · {p.city}, {p.state}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {p.seasons?.length > 0 ? `Current: ${p.seasons[p.seasons.length - 1]?.season_name || 'N/A'}` : 'No seasons set up'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={p.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>{p.is_active !== false ? 'Active' : 'Inactive'}</Badge>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </Card>
              ))}
              {data.programs.length === 0 && <p className="text-center py-8 text-gray-400">No programs yet</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Org Modal */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="bg-white rounded-2xl max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-gray-900 flex items-center gap-2"><Edit className="w-5 h-5 text-orange-500" /> Edit Organization</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Organization Name</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} className="mt-1" /></div>
              <div><Label>Website</Label><Input value={editForm.website} onChange={e => setEditForm({...editForm, website: e.target.value})} className="mt-1" /></div>
            </div>
            <div><Label>Address</Label><Input value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} className="mt-1" /></div>
            <div className="border-t pt-3 mt-3">
              <p className="font-bold text-xs text-gray-500 mb-2">PRIMARY CONTACT</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={editForm.primary_contact_name} onChange={e => setEditForm({...editForm, primary_contact_name: e.target.value})} className="mt-1" /></div>
                <div><Label>Title</Label><Input value={editForm.primary_contact_title} onChange={e => setEditForm({...editForm, primary_contact_title: e.target.value})} className="mt-1" /></div>
              </div>
              <div><Label>Email</Label><Input value={editForm.primary_contact_email} onChange={e => setEditForm({...editForm, primary_contact_email: e.target.value})} className="mt-1" /></div>
            </div>
            <Button onClick={saveOrgEdit} disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600 rounded-xl">{saving ? 'Saving...' : 'Save Changes'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent className="bg-white rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="text-red-600 flex items-center gap-2"><AlertCircle className="w-5 h-5" /> Archive Organization?</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600 mb-4">This will archive <strong>{org.name}</strong> and deactivate all its programs. Users will not be deleted but will lose access.</p>
          <div className="flex gap-3">
            <Button onClick={() => setShowDelete(false)} variant="outline" className="flex-1 rounded-xl">Cancel</Button>
            <Button onClick={deleteOrg} disabled={deleting} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl">{deleting ? 'Archiving...' : 'Archive Organization'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Wins Wall Viewer */}
      <Dialog open={showWinsWall} onOpenChange={setShowWinsWall}>
        <DialogContent className="bg-white rounded-3xl max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }} className="text-[#2D2824] flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#FFD60A]" /> Program Wins Wall
            </DialogTitle>
          </DialogHeader>
          {winsData.length > 0 ? (
            <div className="space-y-4 mt-2">
              {winsData.map((post, i) => (
                <div key={post.id || i} className="p-4 rounded-2xl bg-[#F5F3EF] border border-black/5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#E86C3D]/15 flex items-center justify-center text-lg">
                      {post.user_avatar || '🏆'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-sm text-[#2D2824]">{post.user_name || 'Youth'}</p>
                        {post.user_work_site && <span className="text-[10px] text-[#2D2824]/40">{post.user_work_site}</span>}
                      </div>
                      <h4 className="font-semibold text-[#2D2824] mb-1">{post.title}</h4>
                      <p className="text-sm text-[#2D2824]/70 leading-relaxed">{post.description || post.content}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-[#2D2824]/40">
                        <span>{post.celebrations || 0} celebrations</span>
                        <span>{post.comments?.length || 0} comments</span>
                        <span>{post.created_at ? new Date(post.created_at).toLocaleDateString() : ''}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-8 text-[#2D2824]/40">No wins posted yet</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgDetailPage;
